import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveTable, ResponsiveTableColumn } from '@/components/common/ResponsiveTable';
import { Alert } from '@/components/common/Alert';
import { testimonialService, Testimonial } from '@/services/cms.service';
import { XMarkIcon, PlusIcon, StarIcon } from '@heroicons/react/24/outline';
import '@/styles/dashboard.css';
import '@/styles/testimonials.css';

const ITEMS_PER_PAGE = 10;

export const TestimonialsPage = () => {
  const { t } = useTranslation();
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('validated'); // Par défaut, afficher uniquement les validés
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    rating: 5,
    comment: '',
    avatar_url: '',
    product_id: '',
    status: 'pending' as 'pending' | 'validated' | 'rejected',
    featured_on_home: false
  });

  useEffect(() => {
    loadTestimonials();
  }, [selectedStatus]);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      // Filtrer pour n'afficher que les témoignages validés par défaut
      const filters: { status?: string } = {};
      if (selectedStatus) {
        filters.status = selectedStatus;
      }
      const data = await testimonialService.getAll(filters);
      setTestimonials(data);
      setError(null);
    } catch (err: any) {
      console.error('Erreur lors du chargement des témoignages:', err);
      setError(t('adminTestimonials.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredTestimonials = useMemo(() => {
    let filtered = [...testimonials];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.customer_name?.toLowerCase().includes(query) ||
        t.comment.toLowerCase().includes(query) ||
        t.product_name?.toLowerCase().includes(query)
      );
    }

    if (selectedStatus) {
      filtered = filtered.filter(t => t.status === selectedStatus);
    }

    return filtered;
  }, [testimonials, searchQuery, selectedStatus]);

  const totalPages = Math.ceil(filteredTestimonials.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedTestimonials = filteredTestimonials.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedStatus]);

  const handleAdd = () => {
    setEditingTestimonial(null);
    setFormData({
      customer_name: '',
      customer_email: '',
      rating: 5,
      comment: '',
      avatar_url: '',
      product_id: '',
      status: 'pending',
      featured_on_home: false
    });
    setShowModal(true);
  };

  const handleEdit = (testimonial: Testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      customer_name: testimonial.customer_name || '',
      customer_email: testimonial.customer_email || '',
      rating: testimonial.rating,
      comment: testimonial.comment,
      avatar_url: testimonial.avatar_url || '',
      product_id: testimonial.product_id?.toString() || '',
      status: testimonial.status,
      featured_on_home: testimonial.featured_on_home
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('adminTestimonials.deleteConfirm'))) {
      return;
    }

    try {
      await testimonialService.delete(id);
      await loadTestimonials();
    } catch (err: any) {
      alert(t('adminTestimonials.deleteError') + ': ' + (err.message || t('adminTestimonials.unknownError')));
    }
  };

  const handleValidate = async (id: number) => {
    try {
      await testimonialService.validate(id);
      await loadTestimonials();
      setNotification({ type: 'success', message: t('adminTestimonials.validateSuccess') });
    } catch (err: any) {
      setNotification({ type: 'error', message: t('adminTestimonials.saveError') + ': ' + (err.message || t('adminTestimonials.unknownError')) });
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await testimonialService.deactivate(id);
      await loadTestimonials();
      setNotification({ type: 'success', message: t('adminTestimonials.deactivateSuccess') });
    } catch (err: any) {
      setNotification({ type: 'error', message: t('adminTestimonials.saveError') + ': ' + (err.message || t('adminTestimonials.unknownError')) });
    }
  };

  const handleToggleFeatured = async (testimonial: Testimonial) => {
    try {
      await testimonialService.toggleFeatured(testimonial.id, !testimonial.featured_on_home);
      await loadTestimonials();
      setNotification({ type: 'success', message: t('adminTestimonials.updateSuccess') });
    } catch (err: any) {
      setNotification({ type: 'error', message: t('adminTestimonials.saveError') + ': ' + (err.message || t('adminTestimonials.unknownError')) });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.comment.trim()) {
      setNotification({ type: 'warning', message: t('adminTestimonials.commentRequiredMsg') });
      return;
    }

    try {
      // Préparer les données avec validation
      const data: any = {
        comment: formData.comment.trim()
      };

      // Ajouter les champs optionnels seulement s'ils ont une valeur
      if (formData.customer_name?.trim()) {
        data.customer_name = formData.customer_name.trim();
      }
      if (formData.customer_email?.trim()) {
        data.customer_email = formData.customer_email.trim();
      }
      if (formData.rating) {
        data.rating = parseInt(String(formData.rating), 10) || 5;
      }
      if (formData.avatar_url?.trim()) {
        data.avatar_url = formData.avatar_url.trim();
      }
      if (formData.product_id?.trim()) {
        const productId = parseInt(formData.product_id, 10);
        if (!isNaN(productId)) {
          data.product_id = productId;
        }
      }
      if (formData.status) {
        data.status = formData.status;
      }
      if (formData.featured_on_home !== undefined) {
        data.featured_on_home = formData.featured_on_home;
      }
      data.origin = 'manual';

      console.log('💾 Sauvegarde témoignage avec données:', data);

      if (editingTestimonial) {
        await testimonialService.update(editingTestimonial.id, data);
        console.log('✅ Témoignage mis à jour avec succès');
      } else {
        const result = await testimonialService.create(data);
        console.log('✅ Témoignage créé avec succès:', result);
      }
      setShowModal(false);
      await loadTestimonials();
    } catch (err: any) {
      console.error('❌ Erreur complète:', err);
      console.error('Erreur response:', err.response);
      const errorMessage = err.response?.data?.error || err.message || t('adminTestimonials.unknownError');
      const statusCode = err.response?.status || 'N/A';
      
      // Si l'erreur est 401 (Non authentifié), rediriger vers la page de connexion
      if (statusCode === 401) {
        setNotification({ 
          type: 'error', 
          message: t('adminTestimonials.sessionExpired') 
        });
        // Rediriger vers la page de connexion après un court délai
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }
      
      setNotification({ 
        type: 'error', 
        message: `${t('adminTestimonials.saveError')} (${statusCode}): ${errorMessage}` 
      });
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon
        key={i}
        className={`star-icon ${i < rating ? 'star-filled' : 'star-empty'}`}
      />
    ));
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { className: string; text: string }> = {
      pending: { className: 'status-badge-warning', text: t('adminTestimonials.pending') },
      validated: { className: 'status-badge-success', text: t('adminTestimonials.validated') },
      rejected: { className: 'status-badge-error', text: t('adminTestimonials.rejected') }
    };
    const statusInfo = statusMap[status] || statusMap.pending;
    return (
      <span className={`status-badge ${statusInfo.className}`}>
        {statusInfo.text}
      </span>
    );
  };

  const columns: ResponsiveTableColumn<Testimonial>[] = [
    {
      key: 'customer',
      label: t('adminTestimonials.client'),
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
            {row.customer_name || t('adminTestimonials.anonymous')}
          </div>
          {row.customer_email && (
            <div style={{ fontSize: '12px', color: '#666' }}>{row.customer_email}</div>
          )}
        </div>
      )
    },
    {
      key: 'comment',
      label: t('adminTestimonials.comment'),
      render: (row) => (
        <div className="table-cell-text text-truncate" style={{ maxWidth: '400px' }}>
          {row.comment || t('adminTestimonials.noComment')}
        </div>
      ),
      mobileHidden: true
    },
    {
      key: 'rating',
      label: t('adminTestimonials.rating'),
      render: (row) => (
        <div className="flex items-center gap-1">
          {renderStars(row.rating || 5)}
        </div>
      )
    },
    {
      key: 'status',
      label: t('adminTestimonials.statusLabel'),
      render: (row) => getStatusBadge(row.status)
    },
    {
      key: 'product',
      label: t('adminTestimonials.product'),
      render: (row) => row.product_name || '-',
      mobileHidden: true
    },
    {
      key: 'actions',
      label: t('adminTestimonials.actions'),
      render: (row) => (
        <div className="product-actions">
          {row.status !== 'validated' && (
            <button
              onClick={() => handleValidate(row.id)}
              className="action-btn action-view"
              title={t('adminTestimonials.validate')}
              aria-label={t('adminTestimonials.validateAria')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '32px', minHeight: '32px' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
            </button>
          )}
          {row.status === 'validated' && (
            <button
              onClick={() => handleDeactivate(row.id)}
              className="action-btn action-edit"
              title={t('adminTestimonials.deactivate')}
              aria-label={t('adminTestimonials.deactivateAria')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
            </button>
          )}
          <button
            onClick={() => handleEdit(row)}
            className="action-btn action-edit"
            title={t('adminTestimonials.edit')}
            aria-label={t('adminTestimonials.editAria')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
              <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
            </svg>
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="action-btn action-delete"
            title={t('adminTestimonials.delete')}
            aria-label={t('adminTestimonials.deleteAria')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
          </button>
        </div>
      )
    }
  ];

  return (
    <>
    <div className="table-card">
              <div className="table-header">
                <h3 className="table-title">{t('adminTestimonials.satisfiedClients', { count: filteredTestimonials.length })}</h3>
                <div className="flex gap-2 items-center">
                  {(searchQuery || selectedStatus) && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedStatus('');
                      }}
                      className="btn btn-secondary btn-sm"
                    >
                      {t('adminTestimonials.resetFilters')}
                    </button>
                  )}
                  <button
                    onClick={handleAdd}
                    className="btn btn-primary btn-sm"
                  >
                    {t('adminTestimonials.addTestimonial')}
                  </button>
                </div>
              </div>

              {/* Filtres */}
              <div className="filters-bar">
                <div className="form-group-flex">
                  <label className="form-label">{t('adminTestimonials.search')}</label>
                  <input
                    type="text"
                    placeholder={t('adminTestimonials.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div style={{ minWidth: '150px' }}>
                  <label className="form-label">{t('adminTestimonials.statusLabel')}</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="form-select"
                  >
                    <option value="validated">{t('adminTestimonials.validatedOnly')}</option>
                    <option value="">{t('adminTestimonials.allStatuses')}</option>
                    <option value="pending">{t('adminTestimonials.pending')}</option>
                    <option value="rejected">{t('adminTestimonials.rejected')}</option>
                  </select>
                </div>
              </div>

              {notification && (
                <Alert
                  type={notification.type}
                  message={notification.message}
                  onClose={() => setNotification(null)}
                />
              )}

              {error ? (
                <Alert type="error" message={error} />
              ) : (
                <ResponsiveTable
                  columns={columns}
                  data={paginatedTestimonials}
                  loading={loading}
                loadingMessage={t('adminTestimonials.loading')}
                emptyMessage={
                  searchQuery || selectedStatus
                      ? t('adminTestimonials.noFilters')
                      : t('adminTestimonials.empty')
                }
                  mobileCardTitle={(row) => (
                    <div>
                      <div className="table-cell-name mb-1">
                        {row.customer_name || t('adminTestimonials.anonymous')}
                      </div>
                      <div className="flex items-center gap-1 mb-2">
                        {renderStars(row.rating || 5)}
                      </div>
                      <div className="text-sm text-body">
                        {row.comment && row.comment.length > 0
                          ? (row.comment.length > 100 ? row.comment.substring(0, 100) + '...' : row.comment)
                          : t('adminTestimonials.noComment')}
                      </div>
                    </div>
                  )}
                  mobileCardActions={(row) => (
                    <div className="product-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {row.status !== 'validated' && (
                        <button
                          onClick={() => handleValidate(row.id)}
                          className="action-btn action-view"
                          title={t('adminTestimonials.validate')}
                          aria-label={t('adminTestimonials.validateAria')}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '32px', minHeight: '32px' }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                          </svg>
                        </button>
                      )}
                      {row.status === 'validated' && (
                        <button
                          onClick={() => handleDeactivate(row.id)}
                          className="action-btn action-edit"
                          title={t('adminTestimonials.deactivate')}
                          aria-label={t('adminTestimonials.deactivateAria')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(row)}
                        className="action-btn action-edit"
                        title={t('adminTestimonials.edit')}
                        aria-label={t('adminTestimonials.editAria')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(row.id)}
                        className="action-btn action-delete"
                        title={t('adminTestimonials.delete')}
                        aria-label={t('adminTestimonials.deleteAria')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>
                      </button>
                    </div>
                  )}
                  tableClassName="products-table"
                />
              )}

              {/* Pagination */}
              {!error && totalPages > 1 && (
                <div style={{
                  padding: '20px',
                  borderTop: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {t('adminTestimonials.displaying', { start: startIndex + 1, end: Math.min(endIndex, filteredTestimonials.length), total: filteredTestimonials.length })}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        background: currentPage === 1 ? '#f5f5f5' : 'white',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        opacity: currentPage === 1 ? 0.5 : 1
                      }}
                    >
                      {t('adminTestimonials.previous')}
                    </button>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
    <>

                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              style={{
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                background: currentPage === page ? '#8B204E' : 'white',
                                color: currentPage === page ? 'white' : '#333',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: currentPage === page ? 600 : 400
                              }}
                            >
                              {page}
                            </button>
                          
    </>
  );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{ padding: '8px' }}>...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        background: currentPage === totalPages ? '#f5f5f5' : 'white',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        opacity: currentPage === totalPages ? 0.5 : 1
                      }}
                    >
                      {t('adminTestimonials.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Ajout/Édition */}
            {showModal && (
              <div 
                className="modal-overlay" 
                onClick={() => setShowModal(false)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
              >
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2 id="modal-title" className="modal-title">
                      {editingTestimonial ? t('adminTestimonials.editModal') : t('adminTestimonials.addModal')}
                    </h2>
                    <button
                      onClick={() => setShowModal(false)}
                      className="modal-close"
                      aria-label={t('adminTestimonials.close')}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                      <div className="flex gap-3">
                        <div className="form-group flex-1">
                          <label className="form-label">{t('adminTestimonials.customerName')}</label>
                          <input
                            type="text"
                            className="form-input"
                            value={formData.customer_name}
                            onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                          />
                        </div>
                        <div className="form-group flex-1">
                          <label className="form-label">{t('adminTestimonials.email')}</label>
                          <input
                            type="email"
                            className="form-input"
                            value={formData.customer_email}
                            onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                          />
                        </div>
                      </div>

                    <div className="form-group">
                      <label className="form-label">{t('adminTestimonials.ratingLabel')}</label>
                      <input
                        type="number"
                        className="form-input"
                        value={formData.rating}
                        onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) || 5 })}
                        min="1"
                        max="5"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('adminTestimonials.commentRequired')}</label>
                      <textarea
                        className="form-textarea"
                        value={formData.comment}
                        onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                        rows={6}
                        required
                      />
                    </div>

                      <div className="flex gap-3">
                        <div className="form-group flex-1">
                          <label className="form-label">{t('adminTestimonials.avatarUrl')}</label>
                          <input
                            type="text"
                            className="form-input"
                            value={formData.avatar_url}
                            onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
                            placeholder={t('adminTestimonials.avatarPlaceholder')}
                          />
                        </div>
                        <div className="form-group flex-1">
                          <label className="form-label">{t('adminTestimonials.productId')}</label>
                          <input
                            type="number"
                            className="form-input"
                            value={formData.product_id}
                            onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <div className="form-group flex-1">
                          <label className="form-label">{t('adminTestimonials.statusLabel')}</label>
                          <select
                            className="form-select"
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value as 'pending' | 'validated' | 'rejected' })}
                          >
                            <option value="pending">{t('adminTestimonials.pending')}</option>
                            <option value="validated">{t('adminTestimonials.validated')}</option>
                            <option value="rejected">{t('adminTestimonials.rejected')}</option>
                          </select>
                        </div>
                        <div className="form-group flex-1 flex items-center" style={{ paddingTop: '24px' }}>
                          <label className="form-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.featured_on_home}
                              onChange={(e) => setFormData({ ...formData, featured_on_home: e.target.checked })}
                            />
                            <span>{t('adminTestimonials.featuredHome')}</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="modal-footer">
                      <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="btn btn-secondary"
                      >
                        {t('adminTestimonials.cancel')}
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                      >
                        {editingTestimonial ? t('adminTestimonials.save') : t('adminTestimonials.create')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
    </>
  );
};
